import { useState } from 'react'
import {
  Card, Table, Button, Space, Typography, Tag, Row, Col,
  Tree, Drawer, Form, Input, InputNumber, Select, Switch, Radio, message,
} from 'antd'
import { ApartmentOutlined, PlusOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography
const API = ''

const GROUP_TYPE_COLORS: Record<string, string> = {
  ASSET_CURRENT: 'blue',
  ASSET_NON_CURRENT: 'geekblue',
  LIABILITY_CURRENT: 'red',
  LIABILITY_NON_CURRENT: 'magenta',
  INCOME: 'green',
  DIRECT_COST: 'orange',
  INDIRECT_COST: 'volcano',
  EQUITY: 'purple',
}

function buildTreeData(groups: any[]): any[] {
  const map: Record<string, any> = {}
  groups.forEach(g => { map[g.id] = { ...g, key: g.id, title: g.group_name, children: [] } })
  const roots: any[] = []
  groups.forEach(g => {
    if (g.parent_id && map[g.parent_id]) {
      map[g.parent_id].children.push(map[g.id])
    } else {
      roots.push(map[g.id])
    }
  })
  return roots
}

export default function ChartOfAccountsPage() {
  const { plantId } = usePlantId()
  const qc = useQueryClient()
  const [selectedGroup, setSelectedGroup] = // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useState<any>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [form] = Form.useForm()

  const { data: groupsData, isLoading: gLoading } = useQuery({
    queryKey: ['ledger-groups', plantId],
    queryFn: () =>
      apiClient.get(`${API}/accounting/ledger-groups`, { params: { plant_id: plantId, limit: 500 } }).then(r => r.data),
    enabled: !!plantId,
  })

  const { data: ledgersData, isLoading: lLoading } = useQuery({
    queryKey: ['ledgers', selectedGroup?.id],
    queryFn: () =>
      apiClient.get(`${API}/accounting/ledgers`, {
        params: { group_id: selectedGroup.id, limit: 500 },
      }).then(r => r.data),
    enabled: !!selectedGroup?.id,
  })

  const { data: allGroupsData } = useQuery({
    queryKey: ['ledger-groups-flat', plantId],
    queryFn: () =>
      apiClient.get(`${API}/accounting/ledger-groups`, { params: { plant_id: plantId, limit: 500 } }).then(r => r.data),
    enabled: !!plantId,
  })

  const createMut = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiClient.post(`${API}/accounting/ledgers`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ledgers'] })
      message.success('Ledger created')
      setDrawerOpen(false)
      form.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const groups = Array.isArray(groupsData) ? groupsData : []
  const ledgers = Array.isArray(ledgersData) ? ledgersData : []
  const allGroups = Array.isArray(allGroupsData) ? allGroupsData : []
  const treeData = buildTreeData(groups)

  const formatBalance = (v: number, dr_cr: string) => {
    const amt = Number(v ?? 0)
    return (
      <Space>
        <span style={{ color: dr_cr === 'DR' ? '#52c41a' : '#ff4d4f' }}>
          ₹{amt.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
        </span>
        <Tag color={dr_cr === 'DR' ? 'green' : 'red'} style={{ fontSize: 10 }}>{dr_cr}</Tag>
      </Space>
    )
  }

  const columns = [
    {
      title: 'Code', dataIndex: 'ledger_code', key: 'ledger_code', width: 120,
      render: (v: string) => <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{v}</span>,
    },
    { title: 'Ledger Name', dataIndex: 'ledger_name', key: 'ledger_name', width: 200 },
    {
      title: 'Balance', key: 'balance', width: 160,
      render: (_: unknown, r: any) => r.current_balance != null
        ? formatBalance(r.current_balance, r.balance_dr_cr ?? 'DR')
        : '—',
    },
    {
      title: 'GST', dataIndex: 'gst_applicable', key: 'gst_applicable', width: 60, align: 'center' as const,
      render: (v: boolean) => v ? <CheckOutlined style={{ color: '#52c41a' }} /> : <CloseOutlined style={{ color: '#ccc' }} />,
    },
    {
      title: 'TDS', dataIndex: 'tds_applicable', key: 'tds_applicable', width: 60, align: 'center' as const,
      render: (v: boolean) => v ? <CheckOutlined style={{ color: '#52c41a' }} /> : <CloseOutlined style={{ color: '#ccc' }} />,
    },
    {
      title: 'Active', dataIndex: 'is_active', key: 'is_active', width: 70, align: 'center' as const,
      render: (v: boolean) => <Tag color={v ? 'success' : 'default'}>{v ? 'Yes' : 'No'}</Tag>,
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <ApartmentOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Chart of Accounts
      </Title>
      <Text type="secondary">Ledger groups and account master</Text>

      <Row gutter={16} style={{ marginTop: 16 }}>
        {/* Left: Group Tree */}
        <Col span={8}>
          <Card
            title="Ledger Groups"
            size="small"
            loading={gLoading}
            style={{ minHeight: 500 }}
          >
            <Tree
              treeData={treeData}
              titleRender={(node: any) => (
                <Space>
                  <span>{node.group_name}</span>
                  {node.group_type && (
                    <Tag color={GROUP_TYPE_COLORS[node.group_type] ?? 'default'} style={{ fontSize: 10 }}>
                      {node.group_type?.replace(/_/g, ' ')}
                    </Tag>
                  )}
                </Space>
              )}
              onSelect={(_, { node }: any) => setSelectedGroup(node)}
              selectedKeys={selectedGroup ? [selectedGroup.id] : []}
            />
          </Card>
        </Col>

        {/* Right: Ledgers Table */}
        <Col span={16}>
          <Card
            title={selectedGroup ? `Ledgers — ${selectedGroup.group_name}` : 'Select a group'}
            size="small"
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="small"
                onClick={() => {
                  form.resetFields()
                  if (selectedGroup) form.setFieldsValue({ group_id: selectedGroup.id })
                  setDrawerOpen(true)
                }}
              >
                Add Ledger
              </Button>
            }
          >
            <Table
              dataSource={ledgers}
              columns={columns}
              rowKey="id"
              loading={lLoading}
              size="small"
              pagination={{ pageSize: 20 }}
              locale={{ emptyText: selectedGroup ? 'No ledgers in this group' : 'Select a group from the left panel' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Add Ledger Drawer */}
      <Drawer
        title="Add Ledger"
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); form.resetFields() }}
        width={480}
        footer={
          <Space style={{ float: 'right' }}>
            <Button onClick={() => { setDrawerOpen(false); form.resetFields() }}>Cancel</Button>
            <Button
              type="primary"
              loading={createMut.isPending}
              onClick={() => form.validateFields().then(vals => createMut.mutate(vals))}
            >
              Save
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="ledger_code" label="Ledger Code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="ledger_name" label="Ledger Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="group_id" label="Ledger Group" rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={allGroups.map((g: any) => ({ value: g.id, label: g.group_name }))}
            />
          </Form.Item>
          <Form.Item name="opening_balance" label="Opening Balance">
            <InputNumber style={{ width: '100%' }} min={0} precision={2} addonBefore="₹" />
          </Form.Item>
          <Form.Item name="opening_dr_cr" label="Opening Dr/Cr" initialValue="DR">
            <Radio.Group>
              <Radio value="DR">DR (Debit)</Radio>
              <Radio value="CR">CR (Credit)</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item name="gst_applicable" label="GST Applicable" valuePropName="checked" initialValue={false}>
            <Switch />
          </Form.Item>
          <Form.Item name="tds_applicable" label="TDS Applicable" valuePropName="checked" initialValue={false}>
            <Switch />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  )
}
